import { createTheme, ThemeProvider } from '@mui/material';
import { FC, PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {} from '@mui/x-date-pickers/themeAugmentation';

export const MuiProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          htmlFontSize: 10,
          fontFamily: 'Inter Variable, sans-serif',
          allVariants: {
            color: '#0B1370',
          },
          h1: {
            fontSize: '3.6rem',
            fontWeight: 700,
          },
          h2: {
            fontSize: '2.8rem',
            fontWeight: 700,
          },
          h3: {
            fontSize: '2.4rem',
            fontWeight: 700,
          },
          h4: {
            fontSize: '2rem',
            fontWeight: 600,
          },
          h5: {
            fontSize: '1.8rem',
            fontWeight: 600,
          },
          h6: {
            fontSize: '1.6rem',
            fontWeight: 600,
          },
          subtitle2: {
            color: 'rgba(11, 19, 112, .5)',
          },
        },
        palette: {
          primary: {
            main: '#1555FC',
            light: 'rgba(11, 19, 112, 0.1)',
          },
          secondary: {
            main: 'rgba(255, 220, 102, 1)',
          },
          success: {
            main: 'rgba(76, 172, 17, 1)',
          },
          text: {
            secondary: 'rgba(13, 29, 88, .6)',
          },
          info: {
            main: 'rgba(2, 168, 216, 1)',
          },
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1440,
          },
        },
        components: {
          MuiDialog: {
            styleOverrides: {
              paper: {
                borderRadius: '16px',
              },
            },
          },
          MuiButton: {
            defaultProps: {
              variant: 'contained',
              disableElevation: true,
            },
            styleOverrides: {
              root: ({ theme, ownerState }) => {
                let disabledBackground = 'rgba(0, 0, 0, 0.12)';
                switch (ownerState.color) {
                  case 'primary':
                    disabledBackground = theme.palette.primary.light;
                    break;
                  case 'secondary':
                    disabledBackground = theme.palette.secondary.main;
                    break;
                  case 'success':
                    disabledBackground = theme.palette.success.main;
                    break;
                  case 'inherit':
                    disabledBackground = '#E6F1FF';
                    break;
                  case 'error':
                    disabledBackground = theme.palette.error.light;
                    break;
                  case 'info':
                    disabledBackground = theme.palette.info.light;
                    break;
                  case 'warning':
                    disabledBackground = theme.palette.warning.light;
                    break;
                }
                return {
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&.Mui-disabled': {
                    backgroundColor: disabledBackground,
                    color: 'inherit',
                    opacity: 0.5,
                  },
                };
              },
              containedInherit: {
                backgroundColor: '#E6F1FF',
              },
              sizeLarge: {
                fontSize: '1.7rem',
              },
              sizeMedium: {
                height: 40,
                lineHeight: 1,
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              asterisk: {
                color: 'red',
              },
            },
          },
          MuiFormControl: {
            defaultProps: {
              fullWidth: true,
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiFormLabel-root': {
                  top: -6,
                  '&.Mui-focused, &.MuiInputLabel-shrink': {
                    top: 0,
                  },
                },
              },
            },
          },
          MuiOutlinedInput: {
            defaultProps: {
              size: 'small',
            },
            styleOverrides: {
              root: {
                backgroundColor: '#FFF',
              },
              notchedOutline: {
                borderColor: 'rgba(11, 19, 112, 0.3)',
                borderRadius: '6px',
                boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
              },
            },
          },
          MuiPaginationItem: {
            styleOverrides: {
              text: {
                backgroundColor: '#F4F5F7',
                fontSize: '1.6rem',
                borderRadius: '4px',
                '&.Mui-selected': {
                  backgroundColor: '#1555FC',
                  color: '#fff',
                },
              },
              previousNext: {
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0,0,0,.2)',
                },
              },
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: {
                borderRadius: '8px',
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                minWidth: 'auto',
                '&.Mui-selected': {
                  color: '#1555FC',
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontWeight: 500,
                fontSize: 10,
                height: 20,
                px: 1,
              },
              label: {
                px: 0,
              },
              colorSuccess: {
                color: 'rgba(76, 172, 17, 1)',
                backgroundColor: 'rgba(76, 172, 17, 0.1)',
              },
              colorError: {
                color: 'rgba(236, 83, 17, 1)',
                backgroundColor: 'rgba(236, 83, 17, 0.1)',
              },
              colorInfo: {
                color: 'rgba(11, 19, 112, 1)',
                backgroundColor: 'rgba(11, 19, 112, 0.1)',
              },
            },
          },
          MuiAutocomplete: {
            defaultProps: {
              noOptionsText: t('no_options_text'),
              loadingText: t('loading') + '...',
            },
          },
          MuiDatePicker: {
            defaultProps: {
              format: 'DD.MM.YYYY',
            },
          },
        },
      }),
    []
  );
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
